import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { parseResourceReferencePrice } from "../domain/resourcePriceInput";
import { supabase } from "../lib/supabase";
import type { AppProfile } from "../types";

type ResourceType =
  | "material"
  | "labor"
  | "equipment"
  | "subcontract"
  | "transport"
  | "auxiliary"
  | "waste"
  | "other";

type ResourceRow = {
  resource_id: string;
  code: string;
  name: string;
  resource_type: ResourceType;
  unit: string;
  resource_price_id: string | null;
  unit_price: number | null;
  currency: string | null;
  effective_date: string | null;
  counterparty: string | null;
  source_document_id: string | null;
  notes: string | null;
};

type ResourcePriceHistoryRow = {
  id: string;
  resource_id: string;
  unit_price: number;
  currency: string;
  effective_date: string | null;
  counterparty: string | null;
  status: "candidate" | "approved" | "superseded" | "rejected";
  is_reference: boolean;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
};

type Props = {
  profile: AppProfile;
};

const typeLabels: Record<ResourceType, string> = {
  material: "Materiais",
  labor: "Mão de obra",
  equipment: "Equipamentos",
  subcontract: "Subempreitadas",
  transport: "Transportes",
  auxiliary: "Meios auxiliares",
  waste: "Desperdícios",
  other: "Outros",
};

const typeOptions = Object.entries(typeLabels) as Array<[ResourceType, string]>;

function euro(value: number | null) {
  if (value === null || value === undefined) return "Sem preço";
  return new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(Number(value));
}

export default function PricingResources({ profile }: Props) {
  const canPrice = profile.role === "admin" || profile.role === "estimator";
  const canCreate = profile.role === "admin";

  const [rows, setRows] = useState<ResourceRow[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<ResourceType | "all">("all");
  const [showCreate, setShowCreate] = useState(false);

  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState<ResourceType>("material");
  const [newUnit, setNewUnit] = useState("");

  const [priceFor, setPriceFor] = useState<ResourceRow | null>(null);
  const [priceValue, setPriceValue] = useState("");
  const [priceDate, setPriceDate] = useState(new Date().toISOString().slice(0, 10));
  const [priceCounterparty, setPriceCounterparty] = useState("");
  const [priceNotes, setPriceNotes] = useState("");

  const [historyFor, setHistoryFor] = useState<ResourceRow | null>(null);
  const [historyRows, setHistoryRows] = useState<ResourcePriceHistoryRow[]>([]);
  const [historyBusy, setHistoryBusy] = useState(false);
  const historyRequest = useRef(0);
  const writing = useRef(false);
  const [writeBusy, setWriteBusy] = useState(false);

  async function load() {
    setBusy(true);
    setError("");

    const { data, error: queryError } = await supabase
      .from("current_resource_prices")
      .select("*")
      .order("resource_type", { ascending: true })
      .order("name", { ascending: true });

    if (queryError) {
      setError(queryError.message);
      setRows([]);
    } else {
      setRows((data ?? []) as ResourceRow[]);
    }

    setBusy(false);
  }

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(() => {
    const term = query.trim().toLocaleLowerCase("pt-PT");
    return rows.filter((row) => {
      if (typeFilter !== "all" && row.resource_type !== typeFilter) return false;
      if (!term) return true;
      return (
        row.code.toLocaleLowerCase("pt-PT").includes(term) ||
        row.name.toLocaleLowerCase("pt-PT").includes(term)
      );
    });
  }, [rows, query, typeFilter]);

  async function createResource(event: FormEvent) {
    event.preventDefault();
    if (!canCreate) return;

    setError("");
    setNotice("");

    const code = newCode.trim();
    const name = newName.trim();
    const unit = newUnit.trim();

    if (!code || !name || !unit) {
      setError("Código, nome e unidade são obrigatórios.");
      return;
    }

    const { error: insertError } = await supabase.from("resources").insert({
      code,
      name,
      resource_type: newType,
      unit,
    });

    if (insertError) {
      setError(insertError.message);
      return;
    }

    setNewCode("");
    setNewName("");
    setNewUnit("");
    setNewType("material");
    setShowCreate(false);
    setNotice("Recurso criado. Pode agora registar o respetivo preço L37.");
    await load();
  }

  function openPrice(row: ResourceRow) {
    setPriceFor(row);
    setPriceValue(row.unit_price === null ? "" : String(row.unit_price));
    setPriceDate(new Date().toISOString().slice(0, 10));
    setPriceCounterparty(row.counterparty ?? "");
    setPriceNotes("");
    setError("");
    setNotice("");
  }

  async function loadHistory(row: ResourceRow) {
    const request = ++historyRequest.current;
    setHistoryRows([]);
    setHistoryFor(row);
    setHistoryBusy(true);
    setError("");

    const { data, error: historyError } = await supabase
      .from("resource_prices")
      .select(
        "id,resource_id,unit_price,currency,effective_date,counterparty,status,is_reference,notes,metadata,created_at",
      )
      .eq("resource_id", row.resource_id)
      .order("is_reference", { ascending: false })
      .order("effective_date", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false });

    if (request !== historyRequest.current) return;
    if (historyError) {
      setError(historyError.message);
      setHistoryRows([]);
    } else {
      setHistoryRows((data ?? []) as ResourcePriceHistoryRow[]);
    }

    setHistoryBusy(false);
  }

  async function promoteHistoricalPrice(price: ResourcePriceHistoryRow) {
    if (!canPrice || !historyFor || writing.current || historyBusy) return;
    if (price.resource_id !== historyFor.resource_id) {
      setError("O preço não pertence ao recurso aberto. Volte a carregar o histórico.");
      return;
    }
    const resource = historyFor;
    writing.current = true; setWriteBusy(true);
    setError(""); setNotice("");
    try {
      const { error: rpcError } = await supabase.rpc(
        "promote_resource_price_to_reference", { target_price_id: price.id },
      );
      if (rpcError) throw rpcError;
      setNotice("Preço selecionado como referência atual. A observação e a respetiva origem histórica foram preservadas.");
      await load();
      await loadHistory(resource);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String((cause as { message?: string }).message ?? cause));
    } finally { writing.current = false; setWriteBusy(false); }
  }

  async function savePrice(event: FormEvent) {
    event.preventDefault();
    if (!canPrice || !priceFor || writing.current) return;
    const value = parseResourceReferencePrice(priceValue);
    if (value === null) {
      setError("Indique um preço unitário válido. Um campo vazio não equivale a zero.");
      return;
    }
    writing.current = true; setWriteBusy(true); setError(""); setNotice("");
    try {
      const { error: rpcError } = await supabase.rpc("set_resource_reference_price", {
        target_resource_id: priceFor.resource_id,
        new_unit_price: value,
        new_effective_date: priceDate || null,
        new_counterparty: priceCounterparty.trim() || null,
        new_notes: priceNotes.trim() || null,
      });
      if (rpcError) throw rpcError;
      setNotice("Novo preço de referência guardado. O preço anterior fica preservado no histórico.");
      setPriceFor(null);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String((cause as { message?: string }).message ?? cause));
    } finally { writing.current = false; setWriteBusy(false); }
  }

  return (
    <section className="stack-xl">
      <header className="page-header">
        <div>
          <p className="eyebrow">Gerador de Preços L37</p>
          <h2>Recursos & preços base</h2>
          <p className="muted">
            Materiais, mão de obra, equipamentos, transportes e subempreitadas que
            alimentam as composições. Alterar um preço nunca apaga o histórico.
          </p>
        </div>
        {canCreate ? (
          <button
            className="button button-primary"
            type="button"
            onClick={() => setShowCreate((value) => !value)}
          >
            {showCreate ? "Cancelar" : "Novo recurso"}
          </button>
        ) : null}
      </header>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {notice ? <div className="alert alert-success">{notice}</div> : null}

      {showCreate ? (
        <form className="resource-create card" onSubmit={createResource}>
          <label>
            <span>Código L37</span>
            <input
              placeholder="Ex.: MAT.BET.C30_37"
              value={newCode}
              onChange={(event) => setNewCode(event.target.value)}
            />
          </label>
          <label className="grow">
            <span>Nome</span>
            <input
              placeholder="Ex.: Betão C30/37"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
            />
          </label>
          <label>
            <span>Tipo</span>
            <select
              value={newType}
              onChange={(event) => setNewType(event.target.value as ResourceType)}
            >
              {typeOptions.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Unidade</span>
            <input
              placeholder="m³, kg, h…"
              value={newUnit}
              onChange={(event) => setNewUnit(event.target.value)}
            />
          </label>
          <button className="button button-primary" type="submit">
            Criar
          </button>
        </form>
      ) : null}

      <div className="resource-filters card">
        <label className="grow">
          <span>Pesquisar recurso</span>
          <input
            type="search"
            placeholder="Código ou nome…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <label>
          <span>Tipo</span>
          <select
            value={typeFilter}
            onChange={(event) =>
              setTypeFilter(event.target.value as ResourceType | "all")
            }
          >
            <option value="all">Todos</option>
            {typeOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {busy ? (
        <div className="empty card">A carregar recursos…</div>
      ) : filtered.length === 0 ? (
        <div className="empty card">
          <strong>A biblioteca de recursos ainda está vazia.</strong>
          <span>
            Isto é intencional. Vamos introduzir recursos L37 reais e os respetivos
            preços, sem preencher a base com valores fictícios.
          </span>
        </div>
      ) : (
        <div className="resource-table card">
          <div className="resource-row resource-head">
            <span>Recurso</span>
            <span>Tipo</span>
            <span>Un.</span>
            <span>Preço atual</span>
            <span>Data / origem</span>
            <span />
          </div>

          {filtered.map((row) => (
            <div className="resource-row" key={row.resource_id}>
              <div>
                <strong>{row.name}</strong>
                <small className="mono">{row.code}</small>
              </div>
              <span>{typeLabels[row.resource_type]}</span>
              <span>{row.unit}</span>
              <strong>{euro(row.unit_price)}</strong>
              <div>
                <span>{row.effective_date ?? "—"}</span>
                <small>{row.counterparty ?? "Sem contraparte indicada"}</small>
              </div>
              <div className="resource-row-actions">
                <button
                  className="button button-secondary"
                  type="button"
                  disabled={writeBusy}
                  onClick={() => void loadHistory(row)}
                >
                  Histórico
                </button>
                {canPrice ? (
                  <button
                    className="button button-secondary"
                    type="button"
                    disabled={writeBusy}
                    onClick={() => openPrice(row)}
                  >
                    {row.unit_price === null ? "Definir preço" : "Atualizar"}
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      {historyFor ? (
        <section className="resource-history-panel card">
          <div className="resource-history-head">
            <div>
              <p className="eyebrow">Histórico do recurso</p>
              <h3>{historyFor.name}</h3>
              <p className="muted">
                Observações históricas e preços de referência anteriores. Apenas
                uma linha pode ser a referência atual.
              </p>
            </div>
            <button
              className="button button-secondary"
              type="button"
              disabled={writeBusy}
              onClick={() => {
                historyRequest.current++;
                setHistoryFor(null);
                setHistoryRows([]);
              }}
            >
              Fechar
            </button>
          </div>

          {historyBusy ? (
            <div className="empty">A carregar histórico…</div>
          ) : historyRows.length === 0 ? (
            <div className="empty">
              Ainda não existem observações históricas para este recurso.
            </div>
          ) : (
            <div className="resource-history-list">
              {historyRows.map((price) => {
                const metadata = price.metadata ?? {};
                const sourceFile =
                  typeof metadata.source_file === "string"
                    ? metadata.source_file
                    : null;
                const sourceYear =
                  typeof metadata.source_year === "number" ||
                  typeof metadata.source_year === "string"
                    ? String(metadata.source_year)
                    : null;
                const evidence =
                  typeof metadata.evidence === "string"
                    ? metadata.evidence
                    : null;

                return (
                  <article className="resource-history-row" key={price.id}>
                    <div className="resource-history-price">
                      <strong>{euro(Number(price.unit_price))}</strong>
                      <span>/ {historyFor.unit}</span>
                    </div>

                    <div>
                      <span
                        className={
                          price.is_reference
                            ? "resource-price-status reference"
                            : "resource-price-status " + price.status
                        }
                      >
                        {price.is_reference
                          ? "Preço atual"
                          : price.status === "candidate"
                            ? "Candidato"
                            : price.status === "superseded"
                              ? "Anterior"
                              : price.status === "approved"
                                ? "Aprovado"
                                : "Rejeitado"}
                      </span>
                      <small>
                        {sourceYear
                          ? "Histórico " + sourceYear
                          : price.effective_date ?? "Data não indicada"}
                      </small>
                    </div>

                    <div className="resource-history-source">
                      <strong>
                        {price.counterparty ??
                          (sourceFile ? "Nota histórica L37" : "Registo manual")}
                      </strong>
                      <small title={sourceFile ?? undefined}>
                        {sourceFile
                          ? sourceFile.split("/").slice(-1)[0]
                          : price.notes ?? "Sem origem indicada"}
                      </small>
                      {evidence ? <p>{evidence}</p> : null}
                    </div>

                    <div className="resource-history-action">
                      {!price.is_reference &&
                      canPrice &&
                      price.status !== "rejected" ? (
                        <button
                          className="button button-primary"
                          type="button"
                          disabled={writeBusy || historyBusy}
                          onClick={() => void promoteHistoricalPrice(price)}
                        >
                          Usar como preço atual
                        </button>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      ) : null}

      {priceFor ? (
        <form className="resource-price-panel card" onSubmit={savePrice}>
          <div>
            <p className="eyebrow">Preço de referência</p>
            <h3>{priceFor.name}</h3>
            <p className="muted">
              O valor anterior não é substituído: passa a histórico e este torna-se
              a referência corrente.
            </p>
          </div>
          <label>
            <span>Preço / {priceFor.unit}</span>
            <input
              autoFocus
              inputMode="decimal"
              value={priceValue}
              onChange={(event) => setPriceValue(event.target.value)}
            />
          </label>
          <label>
            <span>Data efetiva</span>
            <input
              type="date"
              value={priceDate}
              onChange={(event) => setPriceDate(event.target.value)}
            />
          </label>
          <label>
            <span>Fornecedor / origem</span>
            <input
              value={priceCounterparty}
              onChange={(event) => setPriceCounterparty(event.target.value)}
            />
          </label>
          <label className="grow">
            <span>Nota</span>
            <input
              placeholder="Ex.: tabela setembro 2026 / proposta fornecedor…"
              value={priceNotes}
              onChange={(event) => setPriceNotes(event.target.value)}
            />
          </label>
          <div className="resource-price-actions">
            <button
              className="button button-secondary"
              type="button"
              disabled={writeBusy}
              onClick={() => setPriceFor(null)}
            >
              Cancelar
            </button>
            <button className="button button-primary" type="submit" disabled={writeBusy}>
              {writeBusy ? "A guardar…" : "Guardar preço"}
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}
